import { useState, useEffect } from 'react'
import { useAuth } from '../lib/AuthContext'
import { supabase } from '../lib/supabase'
import { completaIscrizione, parametriIscrizione, indirizzoDopoConferma } from '../lib/completaIscrizione'

export default function PaginaAuth() {
  const { accedi, registrati, profilo, caricaProfilo } = useAuth()
  const [modalita, setModalita] = useState('login')
  const [caricamento, setCaricamento] = useState(false)
  const [errore, setErrore] = useState('')
  const [form, setForm] = useState({ email: '', password: '', nome: '', cognome: '' })
  const [mostraPassword, setMostraPassword] = useState(false)
  const [registrazioneCompletata, setRegistrazioneCompletata] = useState(false)
  const [attesaConferma, setAttesaConferma] = useState(false)   // registrato, in attesa che clicchi il link nell'email
  const [emailReset, setEmailReset] = useState('')
  const [resetInviato, setResetInviato] = useState(false)
  const [nuovaPassword, setNuovaPassword] = useState('')
  const [confermaPassword, setConfermaPassword] = useState('')
  const [isResetMode, setIsResetMode] = useState(false)
  const [giftToken, setGiftToken] = useState(null)
  const [giftInfo, setGiftInfo] = useState(null)
  const [referralId, setReferralId] = useState(null)      // UUID del mittente
  const [referralCode, setReferralCode] = useState(null)  // codice breve es. FP-MARIO23
  const [referralMittente, setReferralMittente] = useState(null) // { nome, cognome }
  // Schermata benvenuto regalo
  const [giftWelcomeStep, setGiftWelcomeStep] = useState('idle') // 'idle'|'welcome'|'done'
  const [giftWelcomeForm, setGiftWelcomeForm] = useState({ email: '', nome: '', cognome: '', password: '' })
  const [giftWelcomeMostraPassword, setGiftWelcomeMostraPassword] = useState(false)
  // Schermata invito gruppo
  const [invitoGruppoInfo, setInvitoGruppoInfo] = useState(null) // { gruppo_id, coach_id, gruppo_nome, coach_nome }
  const [invitoGruppoErrore, setInvitoGruppoErrore] = useState(null) // messaggio se il gruppo ha raggiunto il limite del piano free del coach
  const [invitoGruppoStep, setInvitoGruppoStep] = useState('idle') // 'idle'|'welcome'|'done'
  const [invitoGruppoForm, setInvitoGruppoForm] = useState({ nome: '', cognome: '', email: '', password: '' })
  const [invitoGruppoMostraPassword, setInvitoGruppoMostraPassword] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const params = new URLSearchParams(window.location.search)
  const pagamentoSuccesso = params.get('pagamento') === 'successo'

  if (pagamentoSuccesso) {
    window.history.replaceState({}, '', '/')
  }

  // Controlla se l'utente arriva dal link di reset password
  useEffect(() => {
    const hash = window.location.hash
    if (hash && hash.includes('type=recovery')) {
      setIsResetMode(true)
      window.history.replaceState({}, '', '/')
    }
  }, [])

  // Controlla se l'utente arriva con un gift token o un referral
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('gift')
    const ref = params.get('ref')
    if (token) {
      setGiftToken(token)
      supabase.from('gift_tokens')
        .select('piano, mesi, mittente_email, stato, scadenza_token, destinatario_email')
        .eq('token', token)
        .eq('stato', 'pending')
        .maybeSingle()
        .then(({ data }) => {
          if (data && new Date(data.scadenza_token) > new Date()) {
            setGiftInfo(data)
            if (data.destinatario_email) {
              setGiftWelcomeForm(f => ({ ...f, email: data.destinatario_email }))
            }
            setGiftWelcomeForm(f => ({ ...f, nome: '', cognome: '', password: '' }))
            setGiftWelcomeStep('welcome')
            window.history.replaceState({}, '', '/')
          } else {
            setModalita('registrati')
          }
        })
    }
    if (ref) {
      setReferralCode(ref)
      setModalita('registrati')
      // Carica nome mittente dal codice breve (due query separate, join non affidabile)
      supabase.from('referral_codes')
        .select('utente_id')
        .eq('codice', ref)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.utente_id) {
            setReferralId(data.utente_id)
            // Carica nome mittente separatamente
            supabase.from('profili')
              .select('nome, cognome')
              .eq('id', data.utente_id)
              .maybeSingle()
              .then(({ data: profilo }) => {
                if (profilo) setReferralMittente(profilo)
              })
          }
        })
    }
    // Invito gruppo per utente non iscritto
    const gruppoId = params.get('invito_gruppo')
    const coachId = params.get('coach')
    if (gruppoId && coachId) {
      window.history.replaceState({}, '', '/')
      // Carica info gruppo e coach
      Promise.all([
        supabase.from('gruppi').select('nome').eq('id', gruppoId).maybeSingle(),
        supabase.from('profili').select('nome, cognome').eq('id', coachId).maybeSingle(),
      ]).then(([{ data: gruppo }, { data: coach }]) => {
        setInvitoGruppoInfo({
          gruppo_id: gruppoId,
          coach_id: coachId,
          gruppo_nome: gruppo?.nome || 'un gruppo',
          coach_nome: coach ? `${coach.nome} ${coach.cognome || ''}`.trim() : 'un coach',
        })
        setInvitoGruppoStep('welcome')
      })
    }
  }, [])

  async function handleSubmit(e) {
    e.preventDefault()
    setErrore('')
    setCaricamento(true)
    try {
      if (modalita === 'login') {
        const { error } = await accedi({ email: form.email, password: form.password })
        if (error) {
          if (error.message?.includes('Invalid login credentials')) {
            // Verifica se l'email è registrata
            const { data: profiloEsistente } = await supabase
              .from('profili').select('id').eq('email', form.email.trim().toLowerCase()).maybeSingle()
            if (!profiloEsistente) {
              setErrore('Questa email non è registrata. Vai su "Registrati" per creare un account.')
            } else {
              setErrore('Password non corretta. Verifica e riprova.')
            }
          } else if (error.message?.includes('Email not confirmed')) {
            setErrore('Email non ancora confermata. Controlla la tua casella.')
          } else {
            setErrore('Errore login: ' + (error.message || 'verifica i dati inseriti'))
          }
        }
      } else {
        if (!form.nome.trim()) { setErrore('Inserisci il nome'); setCaricamento(false); return }
        const { data: datiReg, error } = await registrati(form, indirizzoDopoConferma())
        if (error) {
          if (error.message?.includes('already registered')) setErrore('Email già registrata — accedi')
          else setErrore(error.message || 'Errore durante la registrazione')
          setCaricamento(false)
          return
        }
        // Con la conferma email attiva la registrazione non restituisce una sessione:
        // l'utente deve prima cliccare il link ricevuto. Senza questo avviso resterebbe
        // fermo su una pagina muta, senza capire cosa è successo.
        if (!datiReg?.session) {
          setAttesaConferma(true)
          setForm(f => ({ ...f, password: '' }))
          setCaricamento(false)
          return
        }
        // Conferma disattivata: l'utente è già dentro, si completa subito
        await completaIscrizione(parametriIscrizione())
        setRegistrazioneCompletata(true)
        setForm(f => ({ ...f, password: '' }))
      }
    } catch { setErrore('Errore di connessione') }
    setCaricamento(false)
  }

  // ── GESTIONE INVITO GRUPPO (utente non iscritto) ──
  async function handleInvitoGruppo(e) {
    e.preventDefault()
    setErrore('')
    if (!invitoGruppoForm.nome.trim()) { setErrore('Inserisci il nome'); return }
    if (!invitoGruppoForm.email.trim()) { setErrore('Inserisci la email'); return }
    if (!invitoGruppoForm.password || invitoGruppoForm.password.length < 8) { setErrore('La password deve avere almeno 8 caratteri'); return }
    setCaricamento(true)
    try {
      const email = invitoGruppoForm.email.trim().toLowerCase()
      // Prova registrazione, se già esiste prova login
      const { error: regError } = await registrati({
        email,
        password: invitoGruppoForm.password,
        nome: invitoGruppoForm.nome,
        cognome: invitoGruppoForm.cognome,
      })
      if (regError) {
        const { error: loginError } = await accedi({ email, password: invitoGruppoForm.password })
        if (loginError) { setErrore('Password non corretta. Prova di nuovo.'); setCaricamento(false); return }
      }
      // Ottieni utente appena registrato/loggato
      const { data: { user } } = await supabase.auth.getUser()
      if (user && invitoGruppoInfo) {
        // Aggiorna nome/cognome se già esisteva
        await supabase.from('profili').update({
          nome: invitoGruppoForm.nome,
          cognome: invitoGruppoForm.cognome || null,
        }).eq('id', user.id)
        // Controlla se già membro o pending
        const { data: esistente } = await supabase.from('gruppo_membri')
          .select('stato').eq('gruppo_id', invitoGruppoInfo.gruppo_id).eq('utente_id', user.id).maybeSingle()
        if (!esistente) {
          // Inserisci come pending e crea notifica invito
          const { error: erroreInserimento } = await supabase.from('gruppo_membri').insert({
            gruppo_id: invitoGruppoInfo.gruppo_id,
            utente_id: user.id,
            stato: 'pending',
          })
          if (erroreInserimento) {
            // Il gruppo ha raggiunto il limite del piano Free del coach (bloccato dal database).
            // L'account è comunque stato creato correttamente: solo l'ingresso nel gruppo non è andato a buon fine.
            setInvitoGruppoErrore(
              erroreInserimento.message?.includes('LIMITE_PIANO_FREE')
                ? 'Questo gruppo ha già raggiunto il numero massimo di atleti previsto dal piano del coach. Il tuo account è stato creato: contatta il coach per essere aggiunto appena possibile.'
                : "Non è stato possibile aggiungerti al gruppo. Il tuo account è stato creato comunque: contatta il coach."
            )
            setInvitoGruppoStep('done')
            return
          }
          await supabase.from('notifiche').insert({
            utente_id: user.id,
            tipo: 'invito_gruppo',
            titolo: '👥 Invito gruppo',
            testo: `Il coach ${invitoGruppoInfo.coach_nome} ti ha invitato nel gruppo "${invitoGruppoInfo.gruppo_nome}". Accetta o rifiuta dall'app.`,
            dati: JSON.stringify({
              gruppo_id: invitoGruppoInfo.gruppo_id,
              gruppo_nome: invitoGruppoInfo.gruppo_nome,
              coach_id: invitoGruppoInfo.coach_id,
            }),
          })
        }
        setInvitoGruppoStep('done')
      }
    } catch { setErrore('Errore di connessione. Riprova.') }
    setCaricamento(false)
  }

  // ── GESTIONE BENVENUTO REGALO ──
  async function handleGiftWelcome(e) {
    e.preventDefault()
    setErrore('')
    if (!giftWelcomeForm.nome.trim()) { setErrore('Inserisci il nome'); return }
    if (!giftWelcomeForm.password || giftWelcomeForm.password.length < 8) { setErrore('La password deve avere almeno 8 caratteri'); return }
    setCaricamento(true)
    try {
      const email = giftWelcomeForm.email || giftInfo?.destinatario_email
      const { error: regError } = await registrati({
        email,
        password: giftWelcomeForm.password,
        nome: giftWelcomeForm.nome,
        cognome: giftWelcomeForm.cognome,
      })
      if (regError) {
        // Utente già esiste — accedi con la password scelta
        const { error: loginError } = await accedi({ email, password: giftWelcomeForm.password })
        if (loginError) { setErrore('Password non corretta. Prova di nuovo.'); setCaricamento(false); return }
      }
      // Attiva il gift
      if (giftToken && giftInfo) {
        const { data: { user } } = await supabase.auth.getUser()
        if (user) {
          if (giftWelcomeForm.nome) {
            await supabase.from('profili').update({
              nome: giftWelcomeForm.nome,
              cognome: giftWelcomeForm.cognome || null,
            }).eq('id', user.id)
          }
          const scadenza = new Date()
          scadenza.setMonth(scadenza.getMonth() + giftInfo.mesi)
          await supabase.from('profili').update({
            piano: giftInfo.piano,
            abbonamento_attivo: true,
            regalo: true,
            regalo_da: giftInfo.mittente_email,
            abbonamento_dal: new Date().toISOString(),
            abbonamento_al: scadenza.toISOString(),
          }).eq('id', user.id)
          await supabase.from('gift_tokens').update({
            stato: 'riscattato',
            riscattato_il: new Date().toISOString(),
            riscattato_da: user.id,
          }).eq('token', giftToken)
          await supabase.from('notifiche').insert({
            utente_id: user.id,
            titolo: '🎁 Abbonamento regalo attivato!',
            testo: `Hai ricevuto un abbonamento ${giftInfo.piano} di ${giftInfo.mesi} ${giftInfo.mesi === 1 ? 'mese' : 'mesi'} in regalo. Scadenza: ${scadenza.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}.`,
            tipo: 'regalo',
          })
          setGiftWelcomeStep('done')
          // Aggiorna il profilo in memoria senza reload
          if (user) caricaProfilo(user.id)
        }
      }
    } catch { setErrore('Errore di connessione. Riprova.') }
    setCaricamento(false)
  }

  async function inviaReset() {
    if (!emailReset.trim()) { setErrore('Inserisci la tua email'); return }
    setCaricamento(true)
    setErrore('')
    const { error } = await supabase.auth.resetPasswordForEmail(emailReset.trim(), {
      redirectTo: 'https://fitprogress.it',
    })
    setCaricamento(false)
    if (error) { setErrore('Errore: ' + error.message); return }
    setResetInviato(true)
  }

  async function impostaNuovaPassword() {
    if (!nuovaPassword) { setErrore('Inserisci la nuova password'); return }
    if (nuovaPassword.length < 8) { setErrore('La password deve avere almeno 8 caratteri'); return }
    if (nuovaPassword !== confermaPassword) { setErrore('Le password non coincidono'); return }
    setCaricamento(true)
    setErrore('')
    const { error } = await supabase.auth.updateUser({ password: nuovaPassword })
    setCaricamento(false)
    if (error) { setErrore('Errore: ' + error.message); return }
    setIsResetMode(false)
    setModalita('login')
    setRegistrazioneCompletata(false)
    alert('Password aggiornata! Ora accedi con la nuova password.')
    await supabase.auth.signOut()
  }

  // ── SCHERMATA INVITO GRUPPO ──
  if (invitoGruppoStep === 'done') return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '24px 20px', textAlign: 'center' }} className="fade-in">
      <div style={{ fontSize: 72, marginBottom: 20 }}>{invitoGruppoErrore ? '⚠️' : '🎉'}</div>
      <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 24, marginBottom: 12 }}>
        {invitoGruppoErrore ? 'Account creato' : 'Benvenuto su FitProgress!'}
      </div>
      <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.8, maxWidth: 320 }}>
        {invitoGruppoErrore || (
          <>
            Sei stato registrato e hai ricevuto l'invito al gruppo{' '}
            <strong style={{ color: 'var(--accent)' }}>"{invitoGruppoInfo?.gruppo_nome}"</strong>.<br />
            Apri le notifiche nell'app per accettare l'invito.
          </>
        )}
      </div>
    </div>
  )

  if (invitoGruppoStep === 'welcome' && invitoGruppoInfo) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '24px 20px' }}>
      <div style={{ maxWidth: 400, margin: '0 auto', width: '100%' }} className="fade-in">
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <svg viewBox="0 0 226 38" width="192" height="34" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', margin: '0 auto' }}>
            <circle cx="14" cy="25" r="10" fill="none" stroke="var(--accent)" strokeWidth="3.5"/>
            <circle cx="24" cy="25" r="10" fill="none" stroke="var(--accent)" strokeWidth="3.5"/>
            <text x="41" y="30" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="19" fill="var(--text)">Fit</text>
            <text x="87" y="30" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="19" fill="var(--accent)">Progress</text>
          </svg>
        </div>
        {/* Banner invito */}
        <div style={{ background: 'rgba(87,184,255,0.1)', border: '1px solid rgba(87,184,255,0.35)', borderRadius: 16, padding: '20px', textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>👥</div>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 18, color: 'var(--accent2)', marginBottom: 8 }}>
            Sei stato invitato!
          </div>
          <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.8 }}>
            <strong>{invitoGruppoInfo.coach_nome}</strong> ti invita nel gruppo<br />
            <strong style={{ color: 'var(--accent)', fontSize: 16 }}>"{invitoGruppoInfo.gruppo_nome}"</strong>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8, lineHeight: 1.5 }}>
            Registrati su FitProgress per accettare l'invito e iniziare ad allenarti.
          </div>
        </div>

        <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 15, marginBottom: 16 }}>
          Crea il tuo account
        </div>

        <form onSubmit={handleInvitoGruppo} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="form-group">
              <label className="form-label">Nome *</label>
              <input className="form-input" value={invitoGruppoForm.nome}
                onChange={e => setInvitoGruppoForm(f => ({ ...f, nome: e.target.value }))}
                placeholder="Marco" required autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Cognome</label>
              <input className="form-input" value={invitoGruppoForm.cognome}
                onChange={e => setInvitoGruppoForm(f => ({ ...f, cognome: e.target.value }))}
                placeholder="Rossi" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email *</label>
            <input className="form-input" type="email" value={invitoGruppoForm.email}
              onChange={e => setInvitoGruppoForm(f => ({ ...f, email: e.target.value }))}
              placeholder="marco@email.com" required />
          </div>
          <div className="form-group">
            <label className="form-label">Scegli una password *</label>
            <div style={{ position: 'relative' }}>
              <input className="form-input"
                type={invitoGruppoMostraPassword ? 'text' : 'password'}
                value={invitoGruppoForm.password}
                onChange={e => setInvitoGruppoForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Almeno 8 caratteri" minLength={8} required style={{ paddingRight: 44 }} />
              <button type="button" onClick={() => setInvitoGruppoMostraPassword(v => !v)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 16, padding: 0 }}>
                {invitoGruppoMostraPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          {errore && <div className="alert alert-warn"><span>!</span><span>{errore}</span></div>}
          <button className="btn btn-primary" type="submit" disabled={caricamento} style={{ fontSize: 15, padding: '14px' }}>
            {caricamento ? '⏳ Attendi...' : '👥 Registrati e accetta invito →'}
          </button>
          <button type="button" className="btn btn-ghost"
            onClick={() => { setInvitoGruppoStep('idle'); setModalita('login') }}
            style={{ fontSize: 13 }}>
            Ho già un account → Accedi
          </button>
        </form>
      </div>
    </div>
  )

  // ── SCHERMATA BENVENUTO REGALO ──
  if (giftWelcomeStep === 'done') return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '24px 20px', textAlign: 'center' }} className="fade-in">
      <div style={{ fontSize: 72, marginBottom: 20 }}>🎉</div>
      <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 24, marginBottom: 12 }}>Abbonamento attivato!</div>
      <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.8, maxWidth: 300 }}>
        Il tuo abbonamento <strong style={{ color: 'var(--accent)' }}>{giftInfo?.piano}</strong> da{' '}
        <strong style={{ color: 'var(--accent)' }}>{giftInfo?.mesi} {giftInfo?.mesi === 1 ? 'mese' : 'mesi'}</strong> è attivo.
        Bentornato su FitProgress!
      </div>
    </div>
  )

  if (giftWelcomeStep === 'welcome' && giftInfo) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '24px 20px' }}>
      <div style={{ maxWidth: 400, margin: '0 auto', width: '100%' }} className="fade-in">
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <svg viewBox="0 0 226 38" width="192" height="34" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', margin: '0 auto' }}>
            <circle cx="14" cy="25" r="10" fill="none" stroke="var(--accent)" strokeWidth="3.5"/>
            <circle cx="24" cy="25" r="10" fill="none" stroke="var(--accent)" strokeWidth="3.5"/>
            <text x="41" y="30" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="19" fill="var(--text)">Fit</text>
            <text x="87" y="30" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="19" fill="var(--accent)">Progress</text>
          </svg>
        </div>
        <div style={{ background: 'rgba(127,214,61,0.1)', border: '1px solid var(--accent)', borderRadius: 16, padding: '20px', textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>🎁</div>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: 20, color: 'var(--accent)', marginBottom: 8 }}>Hai ricevuto un regalo!</div>
          <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.8 }}>
            Abbonamento <strong>{giftInfo.piano}</strong> da <strong>{giftInfo.mesi} {giftInfo.mesi === 1 ? 'mese' : 'mesi'}</strong>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Inviato da: {giftInfo.mittente_email}</div>
        </div>
        <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Completa il tuo profilo per attivarlo</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.6 }}>La tua email è già riconosciuta. Scegli nome e password per accedere.</div>
        <form onSubmit={handleGiftWelcome} style={{ display: 'flex', flexDirection: 'column', gap: 14 }} autoComplete="off">
          <div className="form-group">
            <label className="form-label">Email</label>
            <div style={{ position: 'relative' }}>
              <input className="form-input" type="email" value={giftWelcomeForm.email} readOnly
                style={{ background: 'var(--surface2)', color: 'var(--muted)', cursor: 'default', paddingRight: 36 }} />
              <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--accent)' }}>✓</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="form-group">
              <label className="form-label">Nome *</label>
              <input className="form-input" value={giftWelcomeForm.nome}
                onChange={e => setGiftWelcomeForm(f => ({ ...f, nome: e.target.value }))}
                placeholder="Marco" required autoComplete="given-name" />
            </div>
            <div className="form-group">
              <label className="form-label">Cognome</label>
              <input className="form-input" value={giftWelcomeForm.cognome}
                onChange={e => setGiftWelcomeForm(f => ({ ...f, cognome: e.target.value }))}
                placeholder="Rossi" autoComplete="family-name" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Scegli una password *</label>
            <div style={{ position: 'relative' }}>
              <input className="form-input" type={giftWelcomeMostraPassword ? 'text' : 'password'}
                value={giftWelcomeForm.password}
                onChange={e => setGiftWelcomeForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Almeno 8 caratteri" minLength={8} required style={{ paddingRight: 44 }}
                autoComplete="new-password" />
              <button type="button" onClick={() => setGiftWelcomeMostraPassword(v => !v)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 16, padding: 0 }}>
                {giftWelcomeMostraPassword ? '🙈' : '👁️'}
              </button>
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Questa sarà la tua password per accedere a FitProgress</div>
          </div>
          {errore && <div className="alert alert-warn"><span>!</span><span>{errore}</span></div>}
          <button className="btn btn-primary" type="submit" disabled={caricamento} style={{ fontSize: 15, padding: '14px' }}>
            {caricamento ? '⏳ Attendi...' : '🎁 Attiva il mio abbonamento →'}
          </button>
        </form>
      </div>
    </div>
  )

  // Vista reset password (arrivato dal link email)
  if (isResetMode) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '24px 20px' }}>
      <div style={{ maxWidth: 400, margin: '0 auto', width: '100%' }} className="fade-in">
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
      <svg viewBox="0 0 226 38" width="192" height="34" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', margin: '0 auto' }}>
        <circle cx="14" cy="25" r="10" fill="none" stroke="var(--accent)" strokeWidth="3.5"/>
        <circle cx="24" cy="25" r="10" fill="none" stroke="var(--accent)" strokeWidth="3.5"/>
        <text x="41" y="30" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="19" fill="var(--text)">Fit</text>
        <text x="87" y="30" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="19" fill="var(--accent)">Progress</text>
      </svg>
          <div style={{ fontSize: 14, color: 'var(--muted)' }}>Imposta nuova password</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-group">
            <label className="form-label">Nuova password</label>
            <input className="form-input" type="password" value={nuovaPassword} onChange={e => setNuovaPassword(e.target.value)} placeholder="Almeno 8 caratteri" />
          </div>
          <div className="form-group">
            <label className="form-label">Conferma password</label>
            <input className="form-input" type="password" value={confermaPassword} onChange={e => setConfermaPassword(e.target.value)} placeholder="Ripeti la password" />
          </div>
          {errore && <div className="alert alert-warn"><span>!</span><span>{errore}</span></div>}
          <button className="btn btn-primary" onClick={impostaNuovaPassword} disabled={caricamento}>
            {caricamento ? 'Aggiornamento...' : 'Imposta nuova password'}
          </button>
        </div>
      </div>
    </div>
  )

  // Vista richiesta reset (inserisci email)
  if (modalita === 'reset') return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '24px 20px' }}>
      <div style={{ maxWidth: 400, margin: '0 auto', width: '100%' }} className="fade-in">
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
      <svg viewBox="0 0 226 38" width="192" height="34" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', margin: '0 auto' }}>
        <circle cx="14" cy="25" r="10" fill="none" stroke="var(--accent)" strokeWidth="3.5"/>
        <circle cx="24" cy="25" r="10" fill="none" stroke="var(--accent)" strokeWidth="3.5"/>
        <text x="41" y="30" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="19" fill="var(--text)">Fit</text>
        <text x="87" y="30" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="19" fill="var(--accent)">Progress</text>
      </svg>
          <div style={{ fontSize: 14, color: 'var(--muted)' }}>Recupero password</div>
        </div>

        {resetInviato ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="alert alert-success">
              <span>✓</span>
              <div>
                <strong style={{ display: 'block' }}>Email inviata!</strong>
                Controlla la casella di posta di {emailReset} e clicca il link per reimpostare la password.
              </div>
            </div>
            <button className="btn btn-secondary" onClick={() => { setModalita('login'); setResetInviato(false); setEmailReset('') }}>
              Torna al login
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
              Inserisci la tua email e ti mandiamo un link per reimpostare la password.
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={emailReset} onChange={e => setEmailReset(e.target.value)} placeholder="marco@email.com" />
            </div>
            {errore && <div className="alert alert-warn"><span>!</span><span>{errore}</span></div>}
            <button className="btn btn-primary" onClick={inviaReset} disabled={caricamento}>
              {caricamento ? 'Invio in corso...' : 'Invia link di recupero'}
            </button>
            <button className="btn btn-ghost" onClick={() => { setModalita('login'); setErrore('') }}>
              Torna al login
            </button>
          </div>
        )}
      </div>
    </div>
  )

  // Vista login/registrazione normale
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '24px 20px' }}>
      <div style={{ maxWidth: 400, margin: '0 auto', width: '100%' }} className="fade-in">
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
      <svg viewBox="0 0 226 38" width="192" height="34" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', margin: '0 auto' }}>
        <circle cx="14" cy="25" r="10" fill="none" stroke="var(--accent)" strokeWidth="3.5"/>
        <circle cx="24" cy="25" r="10" fill="none" stroke="var(--accent)" strokeWidth="3.5"/>
        <text x="41" y="30" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="19" fill="var(--text)">Fit</text>
        <text x="87" y="30" fontFamily="Syne, sans-serif" fontWeight="800" fontSize="19" fill="var(--accent)">Progress</text>
      </svg>
          <div style={{ fontSize: 14, color: 'var(--muted)' }}>
            {modalita === 'login' ? 'Bentornato' : 'Crea il tuo account'}
          </div>
        </div>

        {pagamentoSuccesso && (
          <div className="alert alert-success" style={{ marginBottom: 20 }}>
            <span>ok</span>
            <div>
              <strong style={{ display: 'block' }}>Pagamento completato!</strong>
              Accedi per entrare nell'app con il tuo nuovo piano.
            </div>
          </div>
        )}

        {attesaConferma && (
          <div className="alert alert-success" style={{ marginBottom: 20 }}>
            <span>📧</span>
            <div>
              <strong style={{ display: 'block' }}>Controlla la tua email</strong>
              Ti abbiamo scritto a <strong>{form.email}</strong>: clicca il link per completare l'iscrizione
              {parametriIscrizione().invito ? ' ed entrare nel gruppo' : ''}.
              Se non lo trovi, guarda nella posta indesiderata.
            </div>
          </div>
        )}

        {registrazioneCompletata && (
          <div className="alert alert-success" style={{ marginBottom: 20 }}>
            <span>ok</span>
            <div>
              <strong style={{ display: 'block' }}>Account creato!</strong>
              Ora puoi accedere con le tue credenziali.
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Banner regalo — visibile solo se arrivato con gift token valido */}
          {giftInfo && modalita === 'registrati' && (
            <div style={{
              background: 'rgba(127,214,61,0.1)', border: '1px solid var(--accent)',
              borderRadius: 12, padding: '14px 16px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 24, marginBottom: 4 }}>🎁</div>
              <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 15, color: 'var(--accent)', marginBottom: 4 }}>
                Hai ricevuto un regalo!
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6 }}>
                Abbonamento <strong style={{ color: 'var(--text)' }}>{giftInfo.piano}</strong> da{' '}
                <strong style={{ color: 'var(--text)' }}>{giftInfo.mesi} {giftInfo.mesi === 1 ? 'mese' : 'mesi'}</strong>
                <br />Registrati per attivarlo automaticamente.
              </div>
            </div>
          )}
          {referralCode && !giftInfo && modalita === 'registrati' && (
            <div style={{
              background: 'rgba(87,184,255,0.08)', border: '1px solid rgba(87,184,255,0.3)',
              borderRadius: 12, padding: '12px 16px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>👋</div>
              <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: 13, color: 'var(--accent2)', marginBottom: 2 }}>
                {referralMittente ? `Invitato da ${referralMittente.nome} ${referralMittente.cognome}!` : 'Sei stato invitato!'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                Registrati e inizia subito ad allenarti, gratis.
              </div>
            </div>
          )}
          {modalita === 'registrati' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Nome</label>
                <input className="form-input" value={form.nome} onChange={e => set('nome', e.target.value)} placeholder="Marco" required />
              </div>
              <div className="form-group">
                <label className="form-label">Cognome</label>
                <input className="form-input" value={form.cognome} onChange={e => set('cognome', e.target.value)} placeholder="Rossi" />
              </div>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="marco@email.com" required />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                className="form-input"
                type={mostraPassword ? 'text' : 'password'}
                value={form.password}
                onChange={e => set('password', e.target.value)}
                placeholder="Almeno 8 caratteri"
                minLength={8}
                required
                style={{ paddingRight: 44 }}
              />
              <button type="button" onClick={() => setMostraPassword(v => !v)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 16, padding: 0, lineHeight: 1 }}>
                {mostraPassword ? (<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>) : (<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>)}
              </button>
            </div>
          </div>

          {/* Link recupero password */}
          {modalita === 'login' && (
            <div style={{ textAlign: 'right', marginTop: -6 }}>
              <button type="button" onClick={() => { setModalita('reset'); setErrore('') }} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}>
                Ho dimenticato la password
              </button>
            </div>
          )}

          {errore && <div className="alert alert-warn"><span>!</span><span>{errore}</span></div>}
          <button className="btn btn-primary" type="submit" disabled={caricamento} style={{ marginTop: 4 }}>
            {caricamento ? 'Attendere...' : modalita === 'login' ? 'Accedi' : 'Crea account'}
          </button>
        </form>

        <div className="divider" style={{ margin: '24px 0' }}>oppure</div>
        <button className="btn btn-secondary" onClick={() => { setModalita(m => m === 'login' ? 'registrati' : 'login'); setErrore(''); setMostraPassword(false) }}>
          {modalita === 'login' ? '+ Crea un nuovo account' : 'Hai gia un account? Accedi'}
        </button>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button onClick={async () => {
            if (navigator.share) {
              try { await navigator.share({ title: 'FitProgress', text: 'Prova FitProgress — l\'app per il tuo allenamento!', url: 'https://fitprogress.it' }) } catch {}
            } else {
              window.open('https://wa.me/?text=' + encodeURIComponent('Prova FitProgress — coaching per calisthenics e strength training! https://fitprogress.it'), '_blank')
            }
          }} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 20px', color: 'var(--muted)', fontSize: 12, cursor: 'pointer', width: '100%' }}>
            📲 Condividi FitProgress
          </button>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10, lineHeight: 1.7 }}>
            Per installare l'app su iPhone:<br/>
            <strong style={{ color: 'var(--text)' }}>Condividi → Aggiungi a schermata Home</strong>
          </div>
        </div>
      </div>
    </div>
  )
}
